import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@fantaz/shared/app-config';
import { WebsocketService } from '@fantaz/shared/data-access/api';
import {
  Address,
  FantazUIError,
  UserGameProfile,
} from '@fantaz/shared/data-access/models';
import { Observable, catchError, map, throwError, timeout } from 'rxjs';
import { UploadService } from './upload-service';
import { UploadedState } from './upload-service.interface';

@Injectable()
export class UploadServiceS3Impl extends UploadService {
  private httpClient = inject(HttpClient);
  private websocketService = inject(WebsocketService);

  override getPresignedUrl(
    image: File,
    options?: { bucketType: string; key: string },
  ): Observable<{ url: string }> {
    return this.httpClient
      .post<{ url: string }>(
        `${environment.apiBaseUrl}/api/client/user/identity/upload-image`,
        { bucketType: 'identity-verification', key: image.name },
      )
      .pipe(
        timeout(31000),
        map(
          (response) => ({
            url: response.url,
          }),
          catchError((error) => {
            if (error.name === 'HttpErrorResponse') {
              return throwError(() => new Error('Connection or timeout issue'));
            }
            return throwError(() => new FantazUIError(error));
          }),
        ),
      );
  }

  override uploadFileToS3(
    preSignedUrl: string,
    file: File,
  ): Observable<UploadedState> {
    return this.httpClient
      .put(preSignedUrl, file, { observe: 'response' })
      .pipe(
        timeout(31000),
        map((response) => ({
          success: response.status === 200,
        })),
        catchError((error) => {
          if (error.name === 'HttpErrorResponse') {
            return throwError(() => new Error('Connection or timeout issue'));
          }
          return throwError(() => new FantazUIError(error));
        }),
      );
  }

  override imageValidation(data: UserGameProfile): Observable<UploadedState> {
    const request = {
      firstName: data.firstName,
      lastName: data.lastName,
      address: this.flattenAddress(data.address as Address),
      action: 'imageValidation',
      uuid: data.userUuid as string,
      email: data.email as string,
    };

    this.websocketService.connect();
    this.websocketService.sendMessage(request);

    return new Observable((observer) => {
      this.websocketService.on()?.subscribe({
        next: (response: any) => {
          observer.next(response);
        },
        error: (e) => {
          observer.error(e);
        },
        complete: () => {
          observer.complete();
        },
      });
    });
  }

  private flattenAddress(legalAddress: Address) {
    const address: string[] = [];
    if (legalAddress?.addressLine1) {
      address.push(legalAddress?.addressLine1);
    }
    if (legalAddress?.city) {
      address.push(legalAddress?.city);
    }
    let zipCode = '';
    if (legalAddress?.state) {
      zipCode += legalAddress?.state;
    }
    if (legalAddress?.zipCode) {
      zipCode += ` ${legalAddress?.zipCode}`;
    }
    if (zipCode) {
      address.push(zipCode);
    }

    return address.join(', ') || '';
  }
}
